import { useMemo } from 'react';
import { Select } from 'antd';
import {
    DEFAULT_COUNTRY, cityOptions, countryOptions, stateName, stateOptions,
} from '../../utils/location';

/**
 * Country → State → City pickers that save full names.
 *
 * `value` is {country, state, city}; `onChange` receives the same shape with
 * the downstream fields cleared when an upstream one changes (a new country
 * empties state and city), so a lead can never say Kerala, Chennai.
 *
 * `show` limits which pickers render — the Update Status form asks only for
 * the ones a stage rule says are missing, but still needs the parent pickers
 * to narrow the list (city needs a state to list cities from).
 *
 * A stored value that is not in the list — free text from an import — still
 * displays, so editing an old lead does not silently blank it.
 */
export default function LocationSelects({
    value = {}, onChange, disabled = false, size = 'large',
    show = ['country', 'state', 'city'], required = [], labelClassName,
    columns = 3,
}) {
    const country = value.country || DEFAULT_COUNTRY;
    const state = stateName(country, value.state);

    const countries = useMemo(() => countryOptions(), []);
    const states = useMemo(() => stateOptions(country), [country]);
    const cities = useMemo(() => cityOptions(country, state), [country, state]);

    const set = (patch) => onChange?.({ country, state, city: value.city || '', ...patch });

    const label = (text, key) => (
        <label className={labelClassName || 'text-xs font-medium text-gray-700 dark:text-gray-300'}>
            {text}{required.includes(key) && <span className="text-red-500"> *</span>}
        </label>
    );

    const filter = (input, opt) => (opt?.label || '').toLowerCase().includes(input.toLowerCase());
    const gridCols = { 1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3' }[columns] || 'sm:grid-cols-3';

    return (
        <div className={`grid grid-cols-1 ${gridCols} gap-4`}>
            {show.includes('country') && (
                <div className="space-y-2">
                    {label('Country', 'country')}
                    <Select size={size} showSearch filterOption={filter} value={country || undefined}
                        onChange={v => set({ country: v, state: '', city: '' })}
                        placeholder="Select country" options={countries} className="w-full" disabled={disabled} />
                </div>
            )}
            {show.includes('state') && (
                <div className="space-y-2">
                    {label('State', 'state')}
                    <Select size={size} showSearch filterOption={filter} value={state || undefined}
                        onChange={v => set({ state: v, city: '' })}
                        placeholder={states.length ? 'Select state' : 'No states listed'}
                        options={states} className="w-full" disabled={disabled} />
                </div>
            )}
            {show.includes('city') && (
                <div className="space-y-2">
                    {label('City', 'city')}
                    <Select size={size} showSearch filterOption={filter} value={value.city || undefined}
                        onChange={v => set({ city: v })}
                        placeholder={state ? 'Select city' : 'Pick a state first'}
                        options={cities} className="w-full" disabled={disabled || !state} />
                </div>
            )}
        </div>
    );
}
